import { InputAdornment, TextField } from "@mui/material";
import React, { memo, useCallback, useMemo, forwardRef } from "react";
import SearchButton, {
  SearchClearButton,
  useSearchInputRef,
} from "./SearchButton";

export const ClearableSearchInput = forwardRef(
  ({ value, disabled, readOnly, ...props }, ref) => {
    const [inputRef, attachRef] = useSearchInputRef(ref);
    return (
      <>
        <input
          {...props}
          data-search-input="true"
          ref={attachRef}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
        />
        {value != null &&
          String(value).length > 0 &&
          !disabled &&
          !readOnly && <SearchClearButton inputRef={inputRef} />}
      </>
    );
  }
);

export const ClearableSearchField = forwardRef(
  ({ value, disabled, inputRef, InputProps = {}, ...props }, ref) => {
    const [searchInputRef, attachRef] = useSearchInputRef(inputRef);
    const canClear =
      value != null &&
      String(value).length > 0 &&
      !disabled &&
      !InputProps.readOnly &&
      !props.inputProps?.readOnly;
    return (
      <TextField
        {...props}
        ref={ref}
        inputRef={attachRef}
        inputProps={{ ...props.inputProps, "data-search-input": "true" }}
        value={value}
        disabled={disabled}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <>
              {canClear && (
                <InputAdornment position="end">
                  <SearchClearButton inputRef={searchInputRef} />
                </InputAdornment>
              )}
              {InputProps.endAdornment}
            </>
          ),
        }}
      />
    );
  }
);

const SearchField = ({
  searchValue,
  setSearchValue,
  getSearchResult,
  tableBorder,
}) => {
  // console.log("SearchField Rendered",);

  const handleSearchFilter = useCallback(
    (e) => {
      setSearchValue(e.target.value);
    },
    [setSearchValue]
  );

  const handleKeyUp = useCallback(
    (e) => {
      if (e.key === "Enter") {
        getSearchResult();
      }
    },
    [getSearchResult]
  );

  const endAdornment = useMemo(
    () => (
      <InputAdornment>
        <SearchButton
          tableBorder={tableBorder}
          getSearchResult={getSearchResult}
        />
      </InputAdornment>
    ),
    [tableBorder, getSearchResult]
  );

  return (
    <ClearableSearchField
      type="text"
      variant="outlined"
      fullWidth
      className="searchButton"
      placeholder="Search..."
      label="Search Filter"
      value={searchValue}
      onChange={handleSearchFilter}
      onKeyUp={handleKeyUp}
      InputProps={{
        endAdornment: endAdornment,
      }}
    />
  );
};

export default memo(SearchField);
